import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter,
  HostListener, Input, Output, signal,
} from '@angular/core';
import { TablerIconComponent } from 'angular-tabler-icons';

export type SectionHeaderSize = 'page' | 'section' | 'compact';

export interface TitleMenuItem {
  value: string;
  label: string;
  icon?: string;      // Tabler-Icon-Name (kebab-case, registriert in shared/icons/necta-icons.ts), z. B. 'chart-bar'.
  sub?: string;       // optionale Unterzeile im Menü-Eintrag
  separator?: boolean; // Trenner statt Eintrag
}

export interface TitleMenu {
  value: string;        // aktive Option (semibold + blauer Check)
  menuLabel?: string;   // optionale Menü-Überschrift
  items: TitleMenuItem[];
}

/**
 * necta.one SectionHeader — flexibler Seitenkopf als Alternative zur Breadcrumb.
 * Ein Bauteil, optionale Slots, drei Dichten über `size`. Portiert aus dem
 * Design-System (SectionHeader.jsx). Der `tabs`-Slot des Originals ist bewusst
 * NICHT enthalten. Styles sind component-encapsulated und nutzen DS-Tokens mit
 * Literal-Fallbacks, damit die Komponente auch außerhalb `.ds-shell` korrekt aussieht.
 */
@Component({
  selector: 'app-section-header',
  standalone: true,
  imports: [TablerIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './section-header.component.html',
  styleUrls: ['./section-header.component.scss'],
})
export class SectionHeaderComponent {
  @Input() size: SectionHeaderSize = 'page';
  @Input() icon?: string;
  @Input() iconProminent = false;
  @Input() enableBack = false;
  @Input() enableMenuToggle = false;
  @Input() eyebrow?: string;
  @Input() title?: string;
  @Input() description?: string;
  @Input() titleMenu?: TitleMenu;

  @Output() back = new EventEmitter<void>();
  @Output() menuToggle = new EventEmitter<void>();
  @Output() titleMenuSelect = new EventEmitter<string>();

  /** Open/Close des Titel-Switcher-Menüs — interner State. */
  protected readonly menuOpen = signal(false);

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  // ---- Slot-Entscheidungen (spiegeln SectionHeader.jsx) ----
  /** Icon + Zurück → Flip-Icon (Kreis kippt bei Hover zum Zurück-Pfeil). */
  protected get showFlipIcon(): boolean { return !!this.icon && this.enableBack; }
  /** Zurück ohne Icon → runder umrandeter Zurück-Button. */
  protected get showBackButton(): boolean { return this.enableBack && !this.icon; }
  /** Icon ohne Zurück und ohne Switcher → statischer Modul-Icon-Kreis. */
  protected get showModuleIcon(): boolean { return !!this.icon && !this.enableBack && !this.titleMenu; }

  protected toggleMenu(): void { this.menuOpen.update((o) => !o); }

  protected pick(item: TitleMenuItem): void {
    if (item.separator) return;
    this.menuOpen.set(false);
    this.titleMenuSelect.emit(item.value);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void { this.menuOpen.set(false); }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    const switcher = this.host.nativeElement.querySelector('.sh__switcher');
    if (switcher && !switcher.contains(event.target as Node)) {
      this.menuOpen.set(false);
    }
  }
}
